import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, of, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

import { CostCenter } from '../../../models/cost-center.model';
import { PaymentService } from '../../../services/payments.service';
import { Doa } from '../../../models/doa.model';
import { DoaService } from '../../../services/doa.service';

@UntilDestroy()
@Component({
  selector: 'app-sap-info',
  templateUrl: './sap-info.component.html',
  styleUrls: ['./sap-info.component.scss'],
  standalone: false,
})
export class SapInfoComponent implements OnInit, OnDestroy {
  _costCenter: CostCenter;
  @Input() set costCenter(val: CostCenter) {
    this.formInitialized = false;
    this._costCenter = val;
    this.showCostCenterData();
  }

  private _label: string;
  @Input() set label(val: string) {
    if (val) {
      this._label = val;
      this.description.setValue(this._label);
    }
  }

  @Output() verifiedCostCenter = new EventEmitter<CostCenter>();

  @Output() sapChanged = new EventEmitter<void>();

  // form items
  sapform: FormGroup;
  description: AbstractControl;
  companycode: AbstractControl;
  businessarea: AbstractControl;
  sapaccountnumber: AbstractControl;
  costcenterId: AbstractControl;
  internalordernumber: AbstractControl;
  allocation: AbstractControl;

  // employee id search
  employeeSearchResults: Observable<Doa[]>;
  employeeSearchLoading = false;
  employeeSearchSubject: Subject<string> = new Subject<string>();
  employeeSelection: Doa;

  invalidCostCenter = false;
  invalidCostCenterMessage = '';

  inProgress = false;
  isVerified = false;
  closeWithWarning = false;

  private formInitialized = false;

  constructor(
    private formBuilder: FormBuilder,
    private paymentService: PaymentService,
    private doaService: DoaService
  ) {}

  ngOnInit(): void {
    this.sapform = this.formBuilder.group({
      description: ['', Validators.compose([Validators.required])],
      companycode: [
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern('^[0-9]{4}$'),
        ]),
      ],
      businessarea: [
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern('^[0-9]{4}$'),
        ]),
      ],
      sapaccountnumber: [
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern('^[0-9]{6}$'),
        ]),
      ],
      costcenter: [
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern('^[0-9]{10}$'),
        ]),
      ],
      internalordernumber: [
        '',
        Validators.compose([Validators.pattern('^[0]{1}[0-9]{5}$')]),
      ],
      allocation: ['', Validators.maxLength(25)],
      poNumber: ['', Validators.compose([])],
      taxExempt: ['', Validators.compose([])],
    });
    this.description = this.sapform.controls['description'];
    this.companycode = this.sapform.controls['companycode'];
    this.businessarea = this.sapform.controls['businessarea'];
    this.sapaccountnumber = this.sapform.controls['sapaccountnumber'];
    this.costcenterId = this.sapform.controls['costcenter'];
    this.internalordernumber = this.sapform.controls['internalordernumber'];
    this.allocation = this.sapform.controls['allocation'];

    this.employeeSearchResults = this.initEmployeeSearch();

    this.showCostCenterData();

    this.sapform.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      if (!this.formInitialized) {
        return;
      }
      this.isVerified = false;
      this.sapChanged.emit();
    });
  }

  ngOnDestroy(): void {
    this.sapform.reset();
  }

  // public methods

  // checks wether sap information is valid
  verifySap() {
    this._costCenter = this.getCostCenter();
    this.invalidCostCenterMessage = '';
    this.inProgress = true;
    this.paymentService
      .verifySap(this._costCenter)
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        this.inProgress = false;
        this._costCenter.verified = result.success;
        if (result.success) {
          this.isVerified = true;
          // sap verified
          this.verifiedCostCenter.emit(this._costCenter);
        } else {
          this.isVerified = false;
          // verification error
          this.invalidCostCenterMessage = result.errorMessage;
        }
      });
  }

  selectEmployee(selection: Doa) {
    this.employeeSelection = selection;
    this.isVerified = false;
  }

  public resetForm() {
    if (this.sapform) {
      this.sapform.reset();
    }
    this.employeeSelection = null;
    this.employeeSearchSubject.next(null);
  }

  // getters

  get employeeSearchPlaceholder() {
    return this.employeeSelection
      ? `#${this.employeeSelection.employee_id} - ${this.employeeSelection.first_name} ${this.employeeSelection.last_name}`
      : 'Type to search for an employee';
  }

  get canVerify(): boolean {
    return !(!this.sapform.valid || this.inProgress || !this.employeeSelection);
  }

  get sapLongAccount(): string {
    const costCenter = this.getCostCenter();
    return costCenter.getLongAccount();
  }

  // private methods
  private initEmployeeSearch(): Observable<Doa[]> {
    return this.employeeSearchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      tap(() => {
        this.employeeSearchLoading = true;
      }),
      switchMap((term: string) => {
        if (!term) {
          return of([]);
        }
        return this.findEmployee(term);
      }),
      tap(() => (this.employeeSearchLoading = false))
    );
  }

  private findEmployee(employeeId: any): Observable<Doa[]> {
    return this.doaService.findEmployee(employeeId);
  }

  private getCostCenter(): CostCenter {
    const costCenter = new CostCenter({});
    costCenter.label = this.description.value;
    costCenter.company_code = this.companycode.value;
    costCenter.business_area = this.businessarea.value;
    costCenter.cost_center_id = this.costcenterId.value;
    costCenter.account_number = this.sapaccountnumber.value;
    costCenter.order_number = this.internalordernumber.value;
    costCenter.allocation = this.allocation.value;

    if (this._costCenter) {
      costCenter.id = this._costCenter.id;
      costCenter.active = this._costCenter.active || false;
    }
    if (this.employeeSelection) {
      costCenter.employee_id = this.employeeSelection.employee_id;
      costCenter.first_name = this.employeeSelection.first_name;
      costCenter.last_name = this.employeeSelection.last_name;
      costCenter.limit_per_po = this.employeeSelection.limit_per_po;
    }

    return costCenter;
  }

  private showCostCenterData() {
    if (!this.sapform || !this._costCenter) {
      return;
    }
    if (!this._label) {
      this.description.setValue(this._costCenter.label || '');
    }

    this.companycode.setValue(this._costCenter.company_code || '');
    this.businessarea.setValue(this._costCenter.business_area || '');
    this.costcenterId.setValue(this._costCenter.cost_center_id || '');
    this.sapaccountnumber.setValue(this._costCenter.account_number || '');
    this.internalordernumber.setValue(this._costCenter.order_number || '');
    this.allocation.setValue(this._costCenter.allocation || '');

    if (this._costCenter.employee_id && !this._costCenter.first_name) {
      this.findDoa();
    } else if (this._costCenter.first_name) {
      this.employeeSelection = new Doa({
        employee_id: this._costCenter.employee_id,
        first_name: this._costCenter.first_name,
        last_name: this._costCenter.last_name,
        limit_per_po: this._costCenter.limit_per_po,
      });
    } else {
      this.employeeSelection = null;
    }

    this.isVerified = this._costCenter.id > 0;
    this.formInitialized = true;
  }

  private findDoa() {
    // find doa records by employee id if it's specified
    this.findEmployee(this._costCenter.employee_id)
      .pipe(untilDestroyed(this), take(1))
      .subscribe((employees: Doa[]) => {
        if (employees && employees.length > 0) {
          this._costCenter.first_name = employees[0].first_name;
          this._costCenter.last_name = employees[0].last_name;
          this._costCenter.limit_per_po = employees[0].limit_per_po;

          this.employeeSelection = new Doa(employees[0]);
        }
      });
  }
}
